import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { Thing, WithContext } from 'schema-dts';

import { hygraphClient } from '@/lib/graphql-clients';
import {
  getSdk,
  SpecialtyPage as HygraphSpecialtyPage,
  SpecialtyPageSidebar,
} from '@/lib/types';
import usePageLogging from '@/hooks/use-page-logging';

import AuthorShortBio from '@/components/author-short-bio';
import DatePublishedModified from '@/components/date-published-modified';
import DefaultLayout from '@/components/layout/default-layout';
import Row from '@/components/row';
import Section from '@/components/section';
import Typography from '@/components/typography';

import { GlobalContextProvider } from '@/context/global-context';
import { RowLayout } from '@/enums/row-layout';
import {
  SpecialtyPage,
  SpecialtyPageStaticDataProps,
} from '@/features/specialty-page';
import SpecialtyHeader from '@/features/specialty-page/components/specialty-header';
import SpecialtyLinkedSection from '@/features/specialty-page/components/specialty-linked-section';
import SpecialtyPageBreadcrumb, {
  buildSpecialtyBreadcrumbs,
} from '@/features/specialty-page/components/specialty-page-breadcrumb';
import SpecialtyTableOfContents from '@/features/specialty-page/components/specialty-table-of-contents';
import SpecialtyPageContextProvider from '@/features/specialty-page/context/specialty-page-context';
import specialtyPageTransformer from '@/features/specialty-page/hygraph/transformers/specialty-page.transformer';
import SpecialtyPageStaticDataService from '@/features/specialty-page/services/specialty-page-static-data.service';
import { getRenderer, getRendererData } from '@/hygraph/renderers';
import HeroRenderer from '@/hygraph/renderers/hero.renderer';
import navigationTransformer from '@/hygraph/transformers/navigation.transformer';
import seoTransformer from '@/hygraph/transformers/seo.transformer';
import { HomeRouterHandleType } from '@/pages/[...slug]';
import { RouteHandlerProps } from '@/router/types.router';
import SchemaService from '@/services/schema-service';
import { buildCanonicalLink } from '@/utils/canonical-link-utils';
import {
  DISCIPLINE_TYPE_PARAM_TO_ENUM,
  isParamAlliedHealth,
  isParamNurse,
  isParamTravelAllied,
} from '@/utils/discipline-utils';
import { routes } from '@/utils/route-utils';

type SpecialtyPageProps = {
  specialtyPage: SpecialtyPage;
  schema?: WithContext<Thing>[];
  preview: boolean;
  specialtyPageStaticData: SpecialtyPageStaticDataProps;
  canonicalLink: string;
};

const Component: NextPage<SpecialtyPageProps> = ({
  specialtyPage,
  schema,
  specialtyPageStaticData,
  preview,
  canonicalLink,
}) => {
  usePageLogging(HomeRouterHandleType.SpecialtyPage);

  const { sidebar, title, seo } = specialtyPage;
  const { navigation, footer } = specialtyPage.templateSetting || {};
  const sidebarComponents = sidebar?.specialtyPageSidebarComponents || [];

  return (
    <GlobalContextProvider
      pageMeta={{
        ...seoTransformer(seo, { title }),
        canonicalLink,
      }}
      schema={schema}
      pageStaticData={{
        navbarNavigation: navigationTransformer(navigation?.nav),
        footerNavigation: navigationTransformer(footer?.nav),
      }}
    >
      <SpecialtyPageContextProvider
        specialty={specialtyPage.specialty}
        specialtyPageStaticData={specialtyPageStaticData}
      >
        <DefaultLayout preview={preview}>
          <main>
            <Section>
              <Row layout={RowLayout.FULL_WIDE}>
                <SpecialtyPageBreadcrumb />
              </Row>

              <Row layout={RowLayout.FULL_WIDE} className='pb-8'>
                <Typography.Heading1 className='mb-4'>
                  {specialtyPage.title}
                </Typography.Heading1>

                {specialtyPage.author && (
                  <SpecialtyHeader
                    author={specialtyPage.author}
                    updatedAt={specialtyPage.modifiedAt}
                    minutes={specialtyPage.estimatedReadingTimeInMinutes}
                  />
                )}
              </Row>

              <HeroRenderer hero={specialtyPage?.hero} />

              <Row layout={RowLayout.ONE_COLUMN_WITH_SIDEBAR} className=''>
                <div>
                  {specialtyPage.tocEnabled && (
                    <SpecialtyTableOfContents
                      components={specialtyPage.components}
                    />
                  )}

                  <div>
                    {specialtyPage.components.map((component, index) => {
                      const Renderer = getRenderer(component.__typename);
                      if (!Renderer) return null;
                      const props = getRendererData({ ...component, index });

                      return (
                        <SpecialtyLinkedSection
                          key={component.id}
                          component={component}
                        >
                          <Renderer {...props} />
                        </SpecialtyLinkedSection>
                      );
                    })}
                  </div>
                </div>
                <div className='flex flex-col gap-8'>
                  {sidebarComponents.map((component, index) => {
                    const Renderer = getRenderer(component.__typename);
                    if (!Renderer) return null;
                    const props = getRendererData({ ...component, index });
                    return <Renderer key={component.id} {...props} />;
                  })}
                </div>
              </Row>
              <div />
            </Section>
            {specialtyPage.author && (
              <Section>
                <div className='px-3 py-8'>
                  <AuthorShortBio {...specialtyPage.author} />

                  <DatePublishedModified
                    datePublished={specialtyPage.publishedAt}
                    dateModified={specialtyPage.modifiedAt}
                    className='layout mx-auto mt-4'
                  />
                </div>
              </Section>
            )}
          </main>
        </DefaultLayout>
      </SpecialtyPageContextProvider>
    </GlobalContextProvider>
  );
};

const getStaticProps: GetStaticProps = async (context) => {
  const slug = context.params?.slug;
  const slugs = Array.isArray(slug) ? slug : [slug];
  const firstSlug = slugs[0];
  const secondSlug = slugs[1];
  const slugIsValid =
    (slugs.length === 3 && isParamNurse(firstSlug)) ||
    (slugs.length === 4 &&
      isParamAlliedHealth(firstSlug) &&
      isParamTravelAllied(secondSlug));

  if (!slugIsValid) return { notFound: true };

  const sdk = getSdk(hygraphClient(context.preview));
  const indexOffset = isParamNurse(firstSlug) ? 0 : 1; // Account for "/allied/travel-allied/", which is sort of hard-coded
  const disciplineSlug = slugs[indexOffset + 1];
  const specialtySlug = slugs[indexOffset + 2];
  const disciplineType = DISCIPLINE_TYPE_PARAM_TO_ENUM[firstSlug];

  const specialtyPagesPromise = sdk.SpecialtyPages({
    where: {
      specialty: {
        slug: specialtySlug,
        discipline: { slug: disciplineSlug, disciplineType },
      },
    },
  });
  const defaultSidebarPromise = sdk.SpecialtyPageSidebars({
    where: { slug: 'default' },
  });

  const [{ specialtyPages }, { specialtyPageSidebars }] = await Promise.all([
    specialtyPagesPromise,
    defaultSidebarPromise,
  ]);

  const hygraphSpecialtyPage = specialtyPages.at(-1) as
    | HygraphSpecialtyPage
    | undefined;
  if (!hygraphSpecialtyPage) return { notFound: true };

  const specialtyPage = specialtyPageTransformer(hygraphSpecialtyPage);
  const defaultSidebar = (specialtyPageSidebars.at(-1) ||
    null) as SpecialtyPageSidebar | null;

  specialtyPage.sidebar ||= defaultSidebar;

  const specialtyPageStaticData =
    await SpecialtyPageStaticDataService.get(specialtyPage);

  return {
    props: {
      slug: slugs,
      specialtyPage,
      specialtyPageStaticData,
      preview: context.preview || false,
      schema: buildSpecialtyPageSchema(specialtyPage),
      canonicalLink: buildCanonicalLink(slugs),
    },
    revalidate: 60, // 1 minute
  };
};

const getStaticPaths: GetStaticPaths = async () => {
  const sdk = getSdk(hygraphClient());
  const { specialtyPages } = await sdk.SpecialtyPages();

  const filterSpecialtyPages = (specialtyPage: SpecialtyPage) =>
    !!specialtyPage.specialty && !!specialtyPage.specialty.discipline;
  const mapSpecialtyPages = (specialtyPage: SpecialtyPage) => ({
    params: {
      slug: routes.specialtyPage(specialtyPage).split('/'),
    },
  });

  const paths = (specialtyPages as HygraphSpecialtyPage[])
    .map(specialtyPageTransformer)
    .filter(filterSpecialtyPages)
    .map(mapSpecialtyPages);

  return {
    paths,
    fallback: 'blocking',
  };
};

function buildSpecialtyPageSchema(page: SpecialtyPage): WithContext<Thing>[] {
  if (page.seo?.noIndex) return []; // Don't bother building schema if page is no-index

  const { images, questions, things } = SchemaService.buildSchemaData(
    page.components,
    { specialty: page.specialty || undefined }
  );
  const pageSchema = SchemaService.buildPageThing(page, images);

  if (questions.length > 0) {
    things.unshift({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: questions,
    });
  }

  const breadcrumbs = buildSpecialtyBreadcrumbs(page.specialty);
  if (breadcrumbs.length > 0) {
    things.unshift(SchemaService.buildBreadcrumbsList(breadcrumbs));
  }

  return [{ '@context': 'https://schema.org', ...pageSchema }, ...things];
}

const SpecialtyPageHandler: RouteHandlerProps = {
  getStaticPaths,
  getStaticProps,
  Component,
};

export default SpecialtyPageHandler;
